<template>
  <div class="topics container py-4">
    <!-- Alert -->
    <div v-if="alert.message" class="mb-3">
      <div
        :class="[
          'alert',
          alert.type === 'success' ? 'alert-success' : 'alert-danger',
        ]"
      >
        {{ alert.message }}
      </div>
    </div>

    <!-- Header & Add Topic Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-pink">Student Topics</h2>
      <button
        class="btn btn-pink"
        data-bs-toggle="modal"
        data-bs-target="#enrollModal"
      >
        <i class="bi bi-plus-lg"></i> Add Topic
      </button>
    </div>

    <!-- Filters -->
    <div class="row g-3 mb-4">
      <div class="col-md-6">
        <select
          class="form-select"
          v-model="selectedGrade"
          @change="onGradeChange"
        >
          <option value="">Select Grade</option>
          <option v-for="g in grades" :key="g.id" :value="g.id">
            {{ g.level }}
          </option>
        </select>
      </div>
      <div class="col-md-6">
        <select
          class="form-select"
          v-model="selectedSubject"
          @change="onSubjectChange"
          :disabled="!selectedGrade"
        >
          <option value="">Select Subject</option>
          <option v-for="s in subjects" :key="s.id" :value="s.id">
            {{ s.name }}
          </option>
        </select>
      </div>
    </div>
    <p class="text-end text-pink mb-2">{{ totalTopics }} Topics</p>
    <!-- Topics Table -->
    <div class="table-responsive mt-3">
      <table class="table table-dark table-hover align-middle">
        <thead class="text-pink">
          <tr>
            <th>Topic</th>
            <th>Description</th>
            <th class="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="3" class="text-center py-4">
              <div class="spinner-border text-pink" role="status"></div>
              <p class="mt-2 text-pink mb-0">Loading Topics...</p>
            </td>
          </tr>
          <tr v-if="!loading && topics.length === 0">
            <td :colspan="3" class="text-center py-4 text-pink">
              <i class="bi bi-person-x fs-2"></i>
              <div class="mt-2">
                {{
                  selectedSubject
                    ? "No Topics Found."
                    : "Please select grade and subject."
                }}
              </div>
            </td>
          </tr>
          <tr v-for="topic in topics" :key="topic.id">
            <td>{{ topic.title }}</td>
            <td>{{ topic.description }}</td>
            <td>
              <button
                class="btn btn-sm btn-warning text-dark fw-semibold"
                @click="openEditModal(topic)"
                data-bs-toggle="modal"
                data-bs-target="#editTopicModal"
              >
                <i class="bi bi-pencil-square me-1"></i> Edit
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-center mt-4">
        <nav v-if="lastPage > 1" class="pagination">
          <ul class="pagination custom-pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="loadTopics(currentPage - 1)">
                Prev
              </button>
            </li>

            <li
              v-for="page in lastPage"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="loadTopics(page)">
                {{ page }}
              </button>
            </li>

            <li
              class="page-item"
              :class="{ disabled: currentPage === lastPage }"
            >
              <button class="page-link" @click="loadTopics(currentPage + 1)">
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Add Topic Modal -->
    <div
      class="modal fade"
      id="enrollModal"
      tabindex="-1"
      aria-labelledby="enrollModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title" id="enrollModalLabel">Add Topic</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <AddTopic @topic-added="onTopicAdded" />
        </div>
      </div>
    </div>

    <!-- Edit Topic Modal -->
    <div
      class="modal fade"
      id="editTopicModal"
      tabindex="-1"
      aria-labelledby="editTopicModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title" id="editTopicModalLabel">Edit Topic</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <EditTopic
            v-if="selectedTopic"
            :topic="selectedTopic"
            @topic-updated="onTopicUpdated"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AddTopic from "./MyTopics/AddTopic.vue";
import EditTopic from "./MyTopics/EditTopic.vue";

export default {
  name: "EnrollmentsView",
  components: { AddTopic, EditTopic },

  data() {
    return {
      grades: [],
      subjects: [],
      topics: [],
      loading: false,
      alert: { type: "", message: "" },
      selectedGrade: "",
      selectedSubject: "",
      selectedTopic: null,

      currentPage: 1,
      lastPage: 1,
      totalTopics: 0,
    };
  },

  methods: {
    async loadGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async loadSubjects(gradeId) {
      try {
        const res = await axios.get(`/a-grade-subjects?grade_id=${gradeId}`);
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({ id: s.subject.id, name: s.subject.name }));
      } catch (err) {
        console.error(err);
        this.subjects = [];
      }
    },

    async loadTopics(page = 1) {
      this.loading = true;

      try {
        let url = "/a-get-topics";

        const params = {
          page: page,
        };

        if (this.selectedSubject) {
          params.subject_id = this.selectedSubject;
        }

        const res = await axios.get(url, { params });

        this.topics = res.data.topics;
        this.currentPage = res.data.current_page;
        this.lastPage = res.data.last_page;
        this.totalTopics = res.data.total;
      } catch (err) {
        console.error(err);
        this.topics = [];
      } finally {
        this.loading = false;
      }
    },

    onTopicAdded(newTopic) {
      // Only add topic if it matches current subject filter
      if (
        !this.selectedSubject ||
        this.selectedSubject === newTopic.subject_id
      ) {
        this.topics.unshift(newTopic);
      }
      this.alert = { type: "success", message: "Topic added successfully!" };
      setTimeout(() => (this.alert.message = ""), 3000);
    },

    onGradeChange() {
      this.selectedSubject = "";
      if (this.selectedGrade) this.loadSubjects(this.selectedGrade);
      else this.subjects = [];
      this.topics = []; // Clear topics when grade changes
    },

    onSubjectChange() {
      if (!this.selectedGrade || !this.selectedSubject) {
        this.alert = {
          type: "danger",
          message: "Please select grade and subject",
        };
        return;
      }

      this.alert.message = "";
      this.loadTopics(1); // reset to page 1
    },

    async openEditModal(topic) {
      try {
        const res = await axios.get(`/a-get-topic/${topic.id}`);
        this.selectedTopic = res.data;
      } catch (error) {
        console.error(error);
      }
    },

    // Replace the topic in topics array after update
    onTopicUpdated(updatedTopic) {
      const index = this.topics.findIndex((t) => t.id === updatedTopic.id);
      if (index !== -1) {
        this.topics.splice(index, 1, updatedTopic);
      }
      this.alert = { type: "success", message: "Topic updated successfully!" };
      setTimeout(() => (this.alert.message = ""), 3000);
    },
  },

  mounted() {
    this.loadGrades();
    this.loadTopics(); // load all topics initially
  },
};
</script>

<style scoped>
.text-pink {
  color: #ff00cc !important;
}
.btn-pink {
  background-color: #ff00cc;
  color: white;
  border: none;
}
.btn-pink:hover {
  background-color: #e600b3;
  color: white;
}
.border-pink {
  border-color: #ff00cc !important;
}
.border-bottom-pink {
  border-bottom: 2px solid #ff00cc !important;
}

/* Pagination Theme */
.custom-pagination .page-link {
  background-color: #1a1a1a;
  color: #ff00cc;
  border: 1px solid #333;
  transition: 0.3s;
}

.custom-pagination .page-link:hover {
  background-color: #ff00cc;
  color: #fff;
  border-color: #ff00cc;
}

/* Active Page */
.custom-pagination .page-item.active .page-link {
  background-color: #ff00cc;
  border-color: #ff00cc;
  color: white;
  font-weight: bold;
}

/* Disabled */
.custom-pagination .page-item.disabled .page-link {
  background-color: #111;
  color: #666;
  border-color: #222;
  cursor: not-allowed;
}
</style>
